import { supabase } from './supabase'
import {
  clientOpere, creerClientApi, definirAccesApi, definirAccesApiClient, lireAccesApi, lireClesApi, lireClientsApi,
  lireComptesClientApi, lireJournalAccesApi, revoquerCleApi,
  type AppelRpc, type ChangementAccesApi, type ChangementAccesClientApi, type FormulaireClientApi, type LigneAccesApi,
} from './accesApi'
import {
  ajouterBoitierAuStock, attribuerBoitier, definirBoitierActif, lireBoitiers, lireBoitiersInconnus, lireJournalBoitiers,
  resumeAlertesGps, santeAlertesGps, synchroniserTraccar,
  lireOrgsPourBoitiers, retirerBoitier,
} from './boitiers'
import { corpsCreationClient, lireAccesClient, traduireErreurClient } from './client'
import { ecartsDeMrr } from './mrr'
import { estPeriode, MOIS_PAYES_ANNUEL_DEFAUT, type PeriodeFacturation, type PrixUnitaires } from './tarification'
import type {
  Changement,
  DonneesPlateforme,
  FormulaireClient,
  IdentifiantsClient,
  ModificationAbonnement,
  Organisation,
  PalierCode,
  Plan,
  RevenuMensuel,
  Source,
  StatutAbonnement,
} from './types'

const PALIERS = new Set<string>(['starter', 'pro', 'business'])
const STATUTS = new Set<string>(['trialing', 'active', 'past_due', 'cancelled'])

const num = (v: unknown): number => {
  const n = Number(v ?? 0)
  return Number.isFinite(n) ? n : 0
}
const txt = (v: unknown): string | null => (typeof v === 'string' && v ? v : null)
const palier = (v: unknown): PalierCode => (typeof v === 'string' && PALIERS.has(v) ? (v as PalierCode) : 'starter')
const statut = (v: unknown): StatutAbonnement | null =>
  typeof v === 'string' && STATUTS.has(v) ? (v as StatutAbonnement) : null

/** Les colonnes sont vérifiées ICI, une fois : une faute de frappe ne devient plus « 0 » en silence. */
function organisation(r: Record<string, unknown>): Organisation {
  return {
    org_id: String(r['org_id']),
    name: String(r['name'] ?? ''),
    brand_name: txt(r['brand_name']),
    is_operator: r['is_operator'] === true,
    is_white_label: r['is_white_label'] === true,
    plan_tier: palier(r['plan_tier']),
    sub_status: statut(r['sub_status']),
    period_end: txt(r['period_end']),
    members: num(r['members']),
    drivers: num(r['drivers']),
    vehicles: num(r['vehicles']),
    courses_30d: num(r['courses_30d']),
    courses_total: num(r['courses_total']),
    revenue_fcfa: num(r['revenue_fcfa']),
    last_payment: txt(r['last_payment']),
    created_at: String(r['created_at'] ?? ''),
    // 0027 : renseignés ensuite par `platform_tarifs()` (voir `charger`).
    extra_vehicles: 0,
    billing_period: 'monthly',
    gps_devices: 0,
    prix_appliques: null,
    mrr_base: null,
    // 0048 : renseigné ensuite par `platform_acces_api()` (voir `charger`).
    acces_api: null,
  }
}

const montant = (v: unknown): number | null => (v === null || v === undefined ? null : num(v))

/** Une ligne de `platform_tarifs()` : options, période et prix réellement appliqués d'une organisation. */
interface FaitsTarif {
  extra_vehicles: number
  billing_period: PeriodeFacturation
  gps_devices: number
  prix_appliques: PrixUnitaires
  mrr_base: number | null
}
function faitsTarif(r: Record<string, unknown>): FaitsTarif {
  return {
    extra_vehicles: Math.max(0, Math.trunc(num(r['extra_vehicles']))),
    billing_period: estPeriode(r['billing_period']) ? r['billing_period'] : 'monthly',
    gps_devices: Math.max(0, Math.trunc(num(r['gps_devices']))),
    prix_appliques: {
      base: montant(r['base_price_fcfa']),
      vehiculeSupp: montant(r['extra_vehicle_price_fcfa']),
      boitier: montant(r['gps_device_price_fcfa']),
      moisPayesAnnuel: r['annual_months_paid'] === null || r['annual_months_paid'] === undefined
        ? MOIS_PAYES_ANNUEL_DEFAUT
        : num(r['annual_months_paid']),
    },
    mrr_base: montant(r['mrr_fcfa']),
  }
}

function plan(r: Record<string, unknown>): Plan {
  return {
    tier: palier(r['tier']),
    name: String(r['name'] ?? ''),
    price_fcfa: r['price_fcfa'] === null || r['price_fcfa'] === undefined ? null : num(r['price_fcfa']),
    max_vehicles: num(r['max_vehicles']),
    // Avant la 0027 ces colonnes n'existent pas : les valeurs de la grille d'octobre 2026 (0053) servent de repli,
    // sauf pour le palier (jamais inventé). Le boîtier GPS (abonnement de suivi) est proposé aux trois paliers.
    extra_vehicle_price_fcfa: 'extra_vehicle_price_fcfa' in r ? montant(r['extra_vehicle_price_fcfa']) : r['tier'] === 'business' ? null : 10000,
    gps_device_price_fcfa: 'gps_device_price_fcfa' in r ? montant(r['gps_device_price_fcfa']) : 5000,
    annual_months_paid: r['annual_months_paid'] === undefined ? MOIS_PAYES_ANNUEL_DEFAUT : num(r['annual_months_paid']),
  }
}

function changement(r: Record<string, unknown>): Changement {
  return {
    id: num(r['id']),
    org_id: String(r['org_id']),
    changed_by: String(r['changed_by']),
    old_tier: r['old_tier'] ? palier(r['old_tier']) : null,
    new_tier: palier(r['new_tier']),
    old_status: statut(r['old_status']),
    new_status: statut(r['new_status']) ?? 'active',
    period_end: txt(r['period_end']),
    note: String(r['note'] ?? ''),
    auteur: txt(r['auteur_nom']) ?? txt(r['auteur_email']),
    created_at: String(r['created_at'] ?? ''),
    old_extra_vehicles: r['old_extra_vehicles'] === null || r['old_extra_vehicles'] === undefined ? null : num(r['old_extra_vehicles']),
    new_extra_vehicles: r['new_extra_vehicles'] === null || r['new_extra_vehicles'] === undefined ? null : num(r['new_extra_vehicles']),
    old_billing_period: estPeriode(r['old_billing_period']) ? r['old_billing_period'] : null,
    new_billing_period: estPeriode(r['new_billing_period']) ? r['new_billing_period'] : null,
  }
}

/**
 * Les colonnes de la 0027 d'abord ; migration pas encore poussée → ancien jeu de colonnes
 * (la console se redéploie seule à chaque push, la base non).
 */
async function lirePlans(): Promise<{ error: { message: string } | null; plans: Plan[] }> {
  let r = await supabase
    .from('plans')
    .select('tier,name,price_fcfa,max_vehicles,extra_vehicle_price_fcfa,gps_device_price_fcfa,annual_months_paid')
    .order('sort_order')
  if (r.error) r = (await supabase.from('plans').select('tier,name,price_fcfa,max_vehicles').order('sort_order')) as typeof r
  return { error: r.error, plans: r.error ? [] : lignes(r.data).map(plan) }
}

/** `supabase.rpc` au format que demandent les enveloppes de `accesApi.ts` (testées avec une fausse fonction). */
const rpc: AppelRpc = (nom, args) => supabase.rpc(nom, args)

const lignes = (d: unknown): Record<string, unknown>[] => (Array.isArray(d) ? (d as Record<string, unknown>[]) : [])

export function sourceSupabase(utilisateurId: string): Source {
  return {
    utilisateurId,

    async charger(): Promise<DonneesPlateforme> {
      const [o, r, p, tarifs, acces] = await Promise.all([
        supabase.rpc('platform_overview'),
        supabase.rpc('platform_revenue_monthly'),
        lirePlans(),
        supabase.rpc('platform_tarifs'),
        // Migration 0048 pas encore poussée (ou RPC en échec) : on n'affiche rien plutôt que de casser la console.
        lireAccesApi(rpc).catch((): LigneAccesApi[] => []),
      ])
      if (o.error) throw new Error(o.error.message)
      const avertissements: string[] = []
      if (r.error) avertissements.push(`Revenus mensuels illisibles (${r.error.message}).`)
      if (p.error) avertissements.push(`Tarifs des plans illisibles (${p.error.message}) : le MRR n'est pas calculé.`)
      const revenus: RevenuMensuel[] = lignes(r.data).map((x) => ({
        month: String(x['month']),
        amount: num(x['amount']),
        payments: num(x['payments']),
      }))
      const faits = new Map<string, FaitsTarif>()
      if (tarifs.error) {
        avertissements.push(
          `Options et prix figés illisibles (${tarifs.error.message}) : le MRR est calculé sur le catalogue, sans véhicules supplémentaires ni boîtiers (migration 0027 à pousser ?).`,
        )
      } else {
        for (const l of lignes(tarifs.data)) faits.set(String(l['org_id']), faitsTarif(l))
      }
      const accesParOrg = new Map(acces.map((l) => [l.org_id, l]))
      const organisations = lignes(o.data).map(organisation).map((org) => {
        const a = accesParOrg.get(org.org_id)
        return { ...org, ...(faits.get(org.org_id) ?? {}), ...(a ? { acces_api: a } : {}) }
      })
      const plans = p.error ? null : p.plans
      if (plans && faits.size > 0) {
        const ecarts = ecartsDeMrr(organisations, plans)
        if (ecarts.length > 0) {
          avertissements.push(
            `Le MRR calculé par la console diffère de celui de la base pour : ${ecarts.join(', ')}. Les règles de prix SQL et TypeScript ont divergé : signalez-le.`,
          )
        }
      }
      return { organisations, revenus, plans, avertissements, chargeLe: new Date() }
    },

    async historique(org: string): Promise<Changement[]> {
      // La RPC rend le NOM de l'auteur (la RLS de `profiles` le masque en
      // lecture directe). Repli sur la table si la fonction n'est pas encore
      // déployée.
      const rpc = await supabase.rpc('platform_journal_abonnement', { p_org: org })
      if (!rpc.error) return lignes(rpc.data).slice(0, 50).map(changement)
      const lire = (colonnes: string) =>
        supabase
          .from('subscription_changes')
          .select(colonnes)
          .eq('org_id', org)
          .order('created_at', { ascending: false })
          .order('id', { ascending: false })
          .limit(50)
      const base = 'id,org_id,changed_by,old_tier,new_tier,old_status,new_status,period_end,note,created_at'
      let r = await lire(`${base},old_extra_vehicles,new_extra_vehicles,old_billing_period,new_billing_period`)
      // Avant la 0027 les colonnes d'options n'existent pas : on relit l'ancien jeu.
      if (r.error) r = await lire(base)
      if (r.error) throw r.error
      return lignes(r.data).map(changement)
    },

    async definirAbonnement(m: ModificationAbonnement): Promise<void> {
      const { error } = await supabase.rpc('platform_definir_abonnement', {
        p_org: m.org,
        p_tier: m.tier,
        p_status: m.status,
        p_period_end: m.finPeriode,
        p_note: m.raison.trim(),
        p_extra_vehicles: m.extraVehicules,
        p_billing_period: m.periode,
      })
      if (error) throw error
    },

    async creerClient(f: FormulaireClient): Promise<IdentifiantsClient> {
      const { data, error } = await supabase.functions.invoke('create-tenant', {
        body: corpsCreationClient(f),
      })
      if (error) {
        // Le détail utile est dans le corps de la réponse, pas dans error.message.
        let code = ''
        let detail = ''
        try {
          const ctx = (error as { context?: { json?: () => Promise<unknown> } }).context
          const j = (await ctx?.json?.()) as { error?: string; detail?: string } | undefined
          code = j?.error ?? ''
          detail = j?.detail ?? ''
        } catch {
          /* corps non JSON : on garde le message brut */
        }
        throw new Error(traduireErreurClient(code, detail, error.message))
      }
      const rep = data as {
        ok?: boolean; error?: string; detail?: string
        email?: string | null; phone?: string | null; temp_password?: string | null; compte_existant?: boolean
        connexion?: string | null; join_code?: string; warning?: string
      } | null
      if (!rep || !rep.ok) throw new Error(traduireErreurClient(rep?.error ?? '', rep?.detail ?? '', ''))
      /* `temp_password: null` = compte déjà existant, ou numéro seul (connexion par code WhatsApp) : jamais « null » à l'écran. */
      return lireAccesClient(rep, f)
    },

    clesApi: (org: string, client?: string | null) => lireClesApi(rpc, org, client ?? null),
    journalAccesApi: (org: string, client?: string | null) => lireJournalAccesApi(rpc, org, client ?? null),
    definirAccesApi: (m: ChangementAccesApi) => definirAccesApi(rpc, m),
    revoquerCleApi: (id: string, note: string) => revoquerCleApi(rpc, id, note),

    clientsApi: () => lireClientsApi(rpc),
    comptesClientApi: (client: string) => lireComptesClientApi(rpc, client),
    definirAccesApiClient: (m: ChangementAccesClientApi) => definirAccesApiClient(rpc, m),
    async clientsOperesPme(orgId: string) {
      const r = await supabase.from('client_accounts').select('id,name,profile_id').eq('org_id', orgId).eq('type', 'pme').order('name')
      if (r.error) throw r.error
      return lignes(r.data).map(clientOpere)
    },
    creerClientApi: (f: FormulaireClientApi) => creerClientApi((nom, options) => supabase.functions.invoke(nom, options), f),

    boitiers: () => lireBoitiers(rpc),
    boitiersInconnus: () => lireBoitiersInconnus(rpc),
    orgsPourBoitiers: () => lireOrgsPourBoitiers(rpc),
    journalBoitiers: (imei?: string | null) => lireJournalBoitiers(rpc, imei ?? null),
    ajouterBoitierAuStock: (imei: string, note: string | null) => ajouterBoitierAuStock(rpc, imei, note),
    attribuerBoitier: (imei: string, org: string, note: string | null) => attribuerBoitier(rpc, imei, org, note),
    retirerBoitier: (imei: string, note: string | null) => retirerBoitier(rpc, imei, note),
    definirBoitierActif: (imei: string, actif: boolean, note: string | null) => definirBoitierActif(rpc, imei, actif, note),
    synchroniserTraccar: (imei: string) => synchroniserTraccar((nom, options) => supabase.functions.invoke(nom, options), imei),
    resumeAlertesGps: () => resumeAlertesGps(rpc),
    santeAlertesGps: () => santeAlertesGps(rpc),
  }
}
